/**
 * Event Configuration Transformation Utilities
 *
 * Handles transformation between user-friendly UI format and device format
 * for event detection configurations.
 *
 * Note: These utilities use metadata from the API (passed as parameter) instead of
 * hardcoded mappings. The metadata comes from the GET /sensors/event-configs/metadata endpoint.
 */

/**
 * Create enum mappings from API metadata
 * @param {Object} metadata - Metadata from API containing enum mappings
 * @returns {Object} Mapping objects for transformations
 */
const createEnumMappings = (metadata) => {
  if (!metadata || !metadata.enums) {
    // Return empty mappings if metadata not available
    return {
      sensorTypeMap: {},
      sensorTypeReverseMap: {},
      parameterMap: {},
      parameterReverseMap: {},
      operatorMap: {},
      operatorReverseMap: {},
      logicMap: {},
      logicReverseMap: {},
      actionMap: {},
      actionReverseMap: {}
    };
  }

  const enums = metadata.enums;

  // Build sensor type mappings
  const sensorTypeMap = {};
  const sensorTypeReverseMap = {};
  if (enums.sensor_types && Array.isArray(enums.sensor_types)) {
    enums.sensor_types.forEach((item) => {
      sensorTypeMap[item.name] = item.value;
      sensorTypeReverseMap[item.value] = item.name;
    });
  }

  // Build parameter mappings with UI-friendly aliases
  const parameterMap = {};
  const parameterReverseMap = {};

  // Map UI names to backend names for parameters
  const parameterAliases = {
    'Accel RMS': 'Acceleration RMS',
    'Accel Peak': 'Acceleration Peak',
    'Accel P2P': 'Acceleration Peak-to-Peak',
    'Velocity RMS': 'Velocity RMS',
    'Velocity Peak': 'Velocity Peak',
    'Velocity P2P': 'Velocity Peak-to-Peak',
    Temperature: 'Overall',
    Overall: 'Overall',
    'Magnetic Flux': 'Magnetic Flux'
  };

  if (enums.parameters) {
    Object.entries(enums.parameters).forEach(([sensorType, params]) => {
      parameterReverseMap[sensorType] = {};
      if (Array.isArray(params)) {
        params.forEach((param) => {
          // Map backend name to its value
          parameterMap[param.name] = param.value;
          parameterReverseMap[sensorType][param.value] = param.name;

          // Also map UI aliases to the same value
          Object.entries(parameterAliases).forEach(([uiName, backendName]) => {
            if (backendName === param.name) {
              parameterMap[uiName] = param.value;
            }
          });
        });
      }
    });
  }

  // Build operator mappings with simple symbols
  const operatorMap = {};
  const operatorReverseMap = {};

  // Map UI operators to backend operator names
  const operatorAliases = {
    '>': 'Greater Than (>)',
    '<': 'Less Than (<)',
    '>=': 'Greater Than or Equal (>=)',
    '<=': 'Less Than or Equal (<=)',
    '=': 'Equal (==)',
    '!=': 'Not Equal (!=)',
    OUTSIDE: 'Outside Range',
    INSIDE: 'Inside Range'
  };

  if (enums.operators && Array.isArray(enums.operators)) {
    enums.operators.forEach((item) => {
      operatorMap[item.name] = item.value;
      operatorReverseMap[item.value] = item.name;

      // Also map UI aliases
      Object.entries(operatorAliases).forEach(([uiSymbol, backendName]) => {
        if (backendName === item.name) {
          operatorMap[uiSymbol] = item.value;
        }
      });
    });
  }

  // Build logic mappings with simple names
  const logicMap = {};
  const logicReverseMap = {};

  if (enums.condition_logic && Array.isArray(enums.condition_logic)) {
    enums.condition_logic.forEach((item) => {
      logicMap[item.name] = item.value;
      logicReverseMap[item.value] = item.name;

      // Also map simple names
      if (item.name.startsWith('AND')) {
        logicMap.AND = item.value;
      } else if (item.name.startsWith('OR')) {
        logicMap.OR = item.value;
      }
    });
  }

  // Build action mappings with simple names
  const actionMap = {};
  const actionReverseMap = {};

  if (enums.action_types && Array.isArray(enums.action_types)) {
    enums.action_types.forEach((item) => {
      actionMap[item.name] = item.value;
      actionReverseMap[item.value] = item.name;

      // Also map simple names
      if (item.name.includes('Immediate Upload')) {
        actionMap['Immediate Upload'] = item.value;
      }
    });
  }

  return {
    sensorTypeMap,
    sensorTypeReverseMap,
    parameterMap,
    parameterReverseMap,
    operatorMap,
    operatorReverseMap,
    logicMap,
    logicReverseMap,
    actionMap,
    actionReverseMap
  };
};

/**
 * Transform user-friendly UI format to device format
 * @param {Array} uiConfigs - Array of event configs in UI format
 * @param {Object} metadata - Metadata from API containing enum mappings
 * @returns {Array} Event configs in device format
 */
/** Temperature event parameter label in metadata (enum display name). */
export const TEMPERATURE_PARAMETER_UI_NAME = 'Overall';

/**
 * Normalize temperature metricType for dropdowns (API uses "Overall", not "Temperature").
 */
export const normalizeTemperatureMetricType = (metric, metricType) => {
  if (metric === 'Temperature' && (!metricType || metricType === 'Temperature')) {
    return TEMPERATURE_PARAMETER_UI_NAME;
  }
  return metricType;
};

/**
 * UI payload when enabling temperature-dashboard coupling on an event rule.
 * Uses location dashboard warning limits (green lower / green upper).
 */
export const buildDashboardCoupledUIEventUpdate = (limits) => {
  const low = limits && limits.green_lower_limit != null
    ? limits.green_lower_limit
    : null;
  const high = limits && limits.green_upper_limit != null
    ? limits.green_upper_limit
    : null;

  const conditions = [];
  if (low != null && high != null) {
    conditions.push({
      metric: 'Temperature',
      metricType: TEMPERATURE_PARAMETER_UI_NAME,
      operator: 'OUTSIDE',
      thresholds: {
        min: { value: low },
        max: { value: high }
      }
    });
  } else {
    if (low != null) {
      conditions.push({
        metric: 'Temperature',
        metricType: TEMPERATURE_PARAMETER_UI_NAME,
        operator: '<',
        thresholds: { value: low }
      });
    }
    if (high != null) {
      conditions.push({
        metric: 'Temperature',
        metricType: TEMPERATURE_PARAMETER_UI_NAME,
        operator: '>',
        thresholds: { value: high }
      });
    }
  }

  return {
    dashboard_coupling_config: { enabled: true },
    coupling_config: null,
    logic: 'OR',
    conditions,
    action: 'Immediate Upload'
  };
};

export const toDeviceFormat = (uiConfigs, metadata) => {
  if (!uiConfigs || uiConfigs.length === 0) {
    return [];
  }

  const maps = createEnumMappings(metadata);
  return uiConfigs.map((uiConfig) => {
    // Transform coupling_config if present
    // Backend expects temperature_threshold_type and vibration_threshold_type
    // Frontend uses threshold_level as a unified value
    let couplingConfig = null;
    if (uiConfig.coupling_config) {
      couplingConfig = { ...uiConfig.coupling_config };
      // Map threshold_level to temperature_threshold_type and vibration_threshold_type
      if (couplingConfig.threshold_level) {
        const thresholdLevel = couplingConfig.threshold_level;
        // Only set if not already present (allow explicit override)
        if (!couplingConfig.temperature_threshold_type) {
          couplingConfig.temperature_threshold_type = thresholdLevel;
        }
        if (!couplingConfig.vibration_threshold_type) {
          couplingConfig.vibration_threshold_type = thresholdLevel;
        }
      }
    }

    let dashboardCouplingConfig = null;
    if (uiConfig.dashboard_coupling_config && !uiConfig.coupling_config) {
      dashboardCouplingConfig = { ...uiConfig.dashboard_coupling_config };
    }

    const deviceConfig = {
      coupling_config: couplingConfig,
      dashboard_coupling_config: dashboardCouplingConfig,
      conditions: uiConfig.conditions.map((condition) => {
        let sensorType = maps.sensorTypeMap[condition.metric];
        let parameter = maps.parameterMap[condition.metricType];
        let operator = maps.operatorMap[condition.operator];
        if (operator === undefined && condition.operator === '<') {
          operator = 1;
        }
        if (operator === undefined && condition.operator === '>') {
          operator = 0;
        }

        if (condition.metric === 'Temperature') {
          if (sensorType === undefined) {
            sensorType = 0;
          }
          if (parameter === undefined) {
            parameter = maps.parameterMap.Temperature != null
              ? maps.parameterMap.Temperature
              : 0;
          }
        }
        const isRangeOperator = condition.operator === 'OUTSIDE' || condition.operator === 'INSIDE';
        const isTriaxial = condition.metric === 'Vibration';

        // Determine axis - default to [0] for single-axis, [1,2,3] for triaxial
        let axis;
        if (condition.axis) {
          axis = condition.axis;
        } else if (isTriaxial) {
          axis = [1, 2, 3]; // Default to all axes for triaxial
        } else {
          axis = [0]; // Default to NA for single-axis
        }

        // Determine threshold format based on axis selection
        let threshold;
        if (isRangeOperator) {
          // Range operators need [min, max] or [[min1, min2, ...], [max1, max2, ...]]
          if (condition.thresholds.min && condition.thresholds.max) {
            if (isTriaxial && axis.length > 0 && axis[0] !== 0) {
              // Triaxial range: map based on selected axes
              const minValues = [];
              const maxValues = [];
              const axisMap = { 1: 'x', 2: 'y', 3: 'z' };

              axis.forEach((axisVal) => {
                const axisKey = axisMap[axisVal];
                minValues.push(parseFloat(condition.thresholds.min[axisKey]) || 0);
                maxValues.push(parseFloat(condition.thresholds.max[axisKey]) || 0);
              });

              threshold = [minValues, maxValues];
            } else {
              // Single-axis range: [min, max]
              threshold = [
                parseFloat(condition.thresholds.min.value) || 0,
                parseFloat(condition.thresholds.max.value) || 0
              ];
            }
          } else if (isTriaxial && axis.length > 0 && axis[0] !== 0) {
            // Fallback: create default range for triaxial
            threshold = [
              new Array(axis.length).fill(0),
              new Array(axis.length).fill(0)
            ];
          } else {
            threshold = [0, 0];
          }
        } else if (isTriaxial && axis.length > 0 && axis[0] !== 0) {
          // Regular operators: map thresholds based on selected axes
          // Triaxial: map based on selected axes
          const thresholdValues = [];
          const axisMap = { 1: 'x', 2: 'y', 3: 'z' };

          axis.forEach((axisVal) => {
            const axisKey = axisMap[axisVal];
            thresholdValues.push(parseFloat(condition.thresholds[axisKey]) || 0);
          });

          threshold = thresholdValues;
        } else {
          // Single value (temperature, magnetometer)
          threshold = [parseFloat(condition.thresholds.value) || 0];
        }

        return {
          sensor_type: sensorType,
          parameter,
          operator,
          axis,
          threshold
        };
      }),
      condition_logic: maps.logicMap[uiConfig.logic],
      actions: [maps.actionMap[uiConfig.action] || 0]
    };

    return deviceConfig;
  });
};

/**
 * Backend now provides UI-friendly display names directly, so no conversion needed.
 * This function is kept for backward compatibility but just returns the name as-is.
 * @param {string} backendName - Display name from backend (already UI-friendly)
 * @returns {string} UI-friendly name (same as input)
 */
const convertBackendNameToUI = backendName => backendName;

/**
 * Transform device format to user-friendly UI format
 * @param {Array} deviceConfigs - Array of event configs in device/backend format
 * @param {Object} metadata - Metadata from API (optional, backend provides display names)
 * @returns {Array} Event configs in UI format
 */
export const toUIFormat = (deviceConfigs, metadata) => {
  if (!deviceConfigs || deviceConfigs.length === 0) {
    return [];
  }

  // Backend provides display names directly, but we can fall back to metadata if needed
  const maps = metadata ? createEnumMappings(metadata) : null;

  return deviceConfigs.map((deviceConfig) => {
    // Transform coupling_config from backend format to UI format
    // Backend sends temperature_threshold_type and vibration_threshold_type
    // UI uses threshold_level as a unified value
    let couplingConfig = null;
    if (deviceConfig.coupling_config) {
      couplingConfig = { ...deviceConfig.coupling_config };
      // If threshold_level already exists, use it (backend may provide it)
      // Otherwise, derive it from temperature_threshold_type and vibration_threshold_type
      if (!couplingConfig.threshold_level) {
        const tempType = couplingConfig.temperature_threshold_type;
        const vibType = couplingConfig.vibration_threshold_type;
        if (tempType && vibType && tempType === vibType) {
          couplingConfig.threshold_level = tempType;
        } else if (tempType || vibType) {
          // If they differ, use the first available
          couplingConfig.threshold_level = tempType || vibType || 'critical';
        }
      }
      // Remove the separate fields to keep UI format clean (UI only uses threshold_level)
      delete couplingConfig.temperature_threshold_type;
      delete couplingConfig.vibration_threshold_type;
    }

    const uiConfig = {
      coupling_config: couplingConfig,
      dashboard_coupling_config: deviceConfig.dashboard_coupling_config
        ? { ...deviceConfig.dashboard_coupling_config }
        : null,
      conditions: deviceConfig.conditions.map((condition) => {
        // Backend provides display names directly (sensor_type_name, operator_name, etc.)
        // Convert them to UI-friendly short names
        let metric = condition.sensor_type_name;
        let metricType = condition.parameter_name;
        let operator = condition.operator_name;

        // Fallback to metadata mapping only if display names aren't present
        if (!metric && maps) {
          metric = maps.sensorTypeReverseMap[condition.sensor_type];
        }
        if (!metricType && maps) {
          const sensorTypeKey = (metric || '').toLowerCase();
          metricType = maps.parameterReverseMap[sensorTypeKey] &&
                       maps.parameterReverseMap[sensorTypeKey][condition.parameter];
        }
        if (!operator && maps) {
          operator = maps.operatorReverseMap[condition.operator];
        }

        // Convert backend display names to UI-friendly short names
        metric = convertBackendNameToUI(metric);
        metricType = convertBackendNameToUI(metricType);
        metricType = normalizeTemperatureMetricType(metric, metricType);
        operator = convertBackendNameToUI(operator);

        // Extract axis field - default to [0] if missing
        const axis = condition.axis || (condition.is_multi_axis ? [1, 2, 3] : [0]);
        const isTriaxial = condition.is_multi_axis || (axis.length > 0 && axis[0] !== 0);

        // Determine thresholds format based on axis selection
        // Check if this is a range operator based on operator name
        const isRangeOperator = operator === 'OUTSIDE' || operator === 'INSIDE';

        let thresholds;
        if (isRangeOperator && condition.threshold && condition.threshold.length === 2) {
          // Range operator: [min, max] or [[min1, min2, ...], [max1, max2, ...]]
          const min = condition.threshold[0];
          const max = condition.threshold[1];

          if (Array.isArray(min) && Array.isArray(max)) {
            // Triaxial range: map based on axis array
            const axisMap = { 1: 'x', 2: 'y', 3: 'z' };
            const minObj = {};
            const maxObj = {};

            axis.forEach((axisVal, idx) => {
              const axisKey = axisMap[axisVal];
              if (axisKey) {
                minObj[axisKey] = min[idx] || 0;
                maxObj[axisKey] = max[idx] || 0;
              }
            });

            thresholds = {
              min: minObj,
              max: maxObj
            };
          } else {
            // Single-axis range: [min, max]
            thresholds = {
              min: { value: min || 0 },
              max: { value: max || 0 }
            };
          }
        } else if (condition.threshold && condition.threshold.length > 0) {
          // Regular operator: map based on axis array
          if (isTriaxial && axis.length > 0 && axis[0] !== 0) {
            // Triaxial: map threshold values to selected axes
            const axisMap = { 1: 'x', 2: 'y', 3: 'z' };
            const thresholdObj = {};

            axis.forEach((axisVal, idx) => {
              const axisKey = axisMap[axisVal];
              if (axisKey && condition.threshold[idx] !== undefined) {
                thresholdObj[axisKey] = condition.threshold[idx];
              }
            });

            thresholds = thresholdObj;
          } else {
            // Single value
            thresholds = {
              value: condition.threshold[0] || 0
            };
          }
        } else {
          // No threshold provided
          thresholds = isTriaxial ? { x: 0, y: 0, z: 0 } : { value: 0 };
        }

        return {
          metric,
          metricType,
          operator,
          axis,
          thresholds,
          is_coupled: condition.is_coupled || false
        };
      }),
      logic: convertBackendNameToUI(deviceConfig.condition_logic_name) ||
             (maps && convertBackendNameToUI(maps.logicReverseMap[deviceConfig.condition_logic])) ||
             'AND',
      action: convertBackendNameToUI((deviceConfig.actions_names && deviceConfig.actions_names[0])) ||
              (maps && convertBackendNameToUI(maps.actionReverseMap[deviceConfig.actions[0]])) ||
              'Immediate Upload'
    };

    return uiConfig;
  });
};

/**
 * Validate event configuration
 * @param {Object} config - Event config in UI format
 * @returns {Object} { isValid: boolean, errors: Array }
 */
export const validateEventConfig = (config) => {
  const errors = [];

  // Check if config exists
  if (!config) {
    return { isValid: false, errors: ['Configuration is required'] };
  }

  // Check conditions
  if (!config.conditions || config.conditions.length === 0) {
    errors.push('At least one condition is required');
  } else {
    // Validate each condition
    config.conditions.forEach((condition, index) => {
      if (!condition.metric) {
        errors.push(`Condition ${index + 1}: Metric is required`);
      }
      if (!condition.metricType) {
        errors.push(`Condition ${index + 1}: Parameter type is required`);
      }
      if (!condition.operator) {
        errors.push(`Condition ${index + 1}: Operator is required`);
      }

      // Validate axis field
      const isTriaxial = condition.metric === 'Vibration';
      let axis = condition.axis;

      if (axis !== undefined) {
        if (!Array.isArray(axis)) {
          errors.push(`Condition ${index + 1}: Axis must be an array`);
        } else if (!isTriaxial) {
          // Single-axis sensors must have axis = [0]
          if (axis.length !== 1 || axis[0] !== 0) {
            errors.push(`Condition ${index + 1}: Single-axis sensors must have axis=[0]`);
          }
        } else {
          // Triaxial sensors: validate axis values are 1, 2, or 3 (at least one)
          const validAxes = [1, 2, 3];
          if (axis.length === 0) {
            errors.push(`Condition ${index + 1}: At least one axis must be selected for triaxial sensors`);
          } else if (!axis.every(a => validAxes.includes(a))) {
            errors.push(`Condition ${index + 1}: Axis array must contain only values 1 (X), 2 (Y), or 3 (Z)`);
          }
        }
      } else {
        // Default axis if not provided
        axis = isTriaxial ? [1, 2, 3] : [0];
      }

      // Validate thresholds
      const isRangeOperator = condition.operator === 'OUTSIDE' || condition.operator === 'INSIDE';

      if (condition.thresholds) {
        if (isRangeOperator) {
          // Range operator validation
          if (condition.thresholds.min && condition.thresholds.max) {
            if (isTriaxial && axis.length > 0 && axis[0] !== 0) {
              // Triaxial range validation - check only selected axes
              const axisMap = { 1: 'x', 2: 'y', 3: 'z' };
              let allValid = true;

              axis.forEach((axisVal) => {
                const axisKey = axisMap[axisVal];
                if (axisKey) {
                  const minVal = condition.thresholds.min[axisKey];
                  const maxVal = condition.thresholds.max[axisKey];
                  const minIsNum = minVal !== undefined && !Number.isNaN(Number(minVal));
                  const maxIsNum = maxVal !== undefined && !Number.isNaN(Number(maxVal));

                  if (!minIsNum || !maxIsNum) {
                    allValid = false;
                  }
                }
              });

              if (!allValid) {
                errors.push(`Condition ${index + 1}: All range threshold values for selected axes must be valid numbers`);
              }
            } else if (condition.thresholds.min.value !== undefined) {
              // Single-axis range validation
              const minIsNum = !Number.isNaN(Number(condition.thresholds.min.value));
              const maxIsNum = !Number.isNaN(Number(condition.thresholds.max.value));

              if (!minIsNum || !maxIsNum) {
                errors.push(`Condition ${index + 1}: Range threshold values must be valid numbers`);
              }
            } else {
              errors.push(`Condition ${index + 1}: Range thresholds (min and max) are required`);
            }
          } else {
            errors.push(`Condition ${index + 1}: Range thresholds (min and max) are required`);
          }
        } else if (isTriaxial && axis.length > 0 && axis[0] !== 0) {
          // Regular operator validation
          // Triaxial validation - check only selected axes
          const axisMap = { 1: 'x', 2: 'y', 3: 'z' };
          let allValid = true;
          let hasValues = false;

          axis.forEach((axisVal) => {
            const axisKey = axisMap[axisVal];
            if (axisKey && condition.thresholds[axisKey] !== undefined) {
              hasValues = true;
              const valIsNum = !Number.isNaN(Number(condition.thresholds[axisKey]));
              if (!valIsNum) {
                allValid = false;
              }
            }
          });

          if (!hasValues) {
            errors.push(`Condition ${index + 1}: Threshold values are required for selected axes`);
          } else if (!allValid) {
            errors.push(`Condition ${index + 1}: All threshold values for selected axes must be valid numbers`);
          }
        } else if (condition.thresholds.value !== undefined) {
          // Single value validation
          const valueIsNum = !Number.isNaN(Number(condition.thresholds.value));
          if (!valueIsNum) {
            errors.push(`Condition ${index + 1}: Threshold value must be a valid number`);
          }
        } else {
          errors.push(`Condition ${index + 1}: Threshold values are required`);
        }
      } else {
        errors.push(`Condition ${index + 1}: Thresholds are required`);
      }
    });
  }

  // Check logic
  if (!config.logic || !['AND', 'OR'].includes(config.logic)) {
    errors.push('Valid condition logic (AND/OR) is required');
  }

  // Check action
  if (!config.action) {
    errors.push('Action is required');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate array of event configurations
 * @param {Array} configs - Array of event configs
 * @returns {Object} { isValid: boolean, errors: Array }
 */
export const validateEventConfigs = (configs) => {
  if (!configs || configs.length === 0) {
    return { isValid: true, errors: [] };
  }

  const allErrors = [];
  let allValid = true;

  configs.forEach((config, index) => {
    const { isValid, errors } = validateEventConfig(config);
    if (!isValid) {
      allValid = false;
      allErrors.push(`Event ${index + 1}: ${errors.join(', ')}`);
    }
  });

  return {
    isValid: allValid,
    errors: allErrors
  };
};

/**
 * Get parameters for a specific sensor type
 * @param {string} sensorType - Sensor type (Vibration, Temperature, Magnetometer)
 * @param {Object} metadata - Metadata from API containing enum mappings
 * @returns {Array} Array of parameter options
 */
export const getParametersForSensorType = (sensorType, metadata) => {
  if (!metadata || !metadata.enums || !metadata.enums.parameters) {
    return [];
  }

  const sensorTypeKey = sensorType.toLowerCase();
  const parameters = metadata.enums.parameters[sensorTypeKey];

  if (!parameters) {
    return [];
  }

  // Transform to the format expected by UI components
  return Object.entries(parameters).map(([, param]) => ({
    value: param.name,
    label: param.name,
    unit: param.unit || '',
    enumValue: param.value
  }));
};

/**
 * Check if operator requires range values (two thresholds)
 * @param {string} operator - Operator string
 * @returns {boolean} True if range operator
 */
export const isRangeOperator = operator => operator === 'OUTSIDE' || operator === 'INSIDE';

/**
 * Check if sensor type is triaxial (requires X, Y, Z values)
 * @param {string} sensorType - Sensor type
 * @returns {boolean} True if triaxial
 */
export const isTriaxialSensor = sensorType => sensorType === 'Vibration';
